import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowForward } from '@mui/icons-material';

import InfoTooltip from 'components/v2/common/infoTooltip';
import { RevenueAtRisk } from 'hooks/analytics';
import { formatCurrency } from 'utils/formatters';

import { SectionCard, SectionTitle } from '../../components/analyticsUi';
import { TODAY_REVENUE_AT_RISK } from '../../constants/metricDescriptions';

interface RevenueAtRiskSectionProps {
    data: RevenueAtRisk;
}

const tileSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    bgcolor: 'background.paper',
    p: 2,
    minWidth: 0,
    boxSizing: 'border-box' as const,
};

const detailCardSx = {
    ...tileSx,
    bgcolor: 'action.hover',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    minHeight: { xs: 72, sm: 88 },
    '&:hover': {
        bgcolor: 'action.selected',
        borderColor: 'primary.main',
    },
};

const RevenueAtRiskSection = ({ data }: RevenueAtRiskSectionProps) => {
    const navigate = useNavigate();
    const goToRevenue = () => navigate('/account/analytics/revenue');

    const trendColor =
        data.trend.direction === 'up'
            ? 'error.main'
            : data.trend.direction === 'down'
              ? 'success.main'
              : 'text.secondary';

    return (
        <SectionCard>
            <SectionTitle>
                Revenue at Risk
                <InfoTooltip title={TODAY_REVENUE_AT_RISK} />
            </SectionTitle>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: 'minmax(0, 1.1fr) minmax(0, 1fr) minmax(0, 1fr)',
                    },
                    gap: 1.5,
                    width: '100%',
                    minWidth: 0,
                    alignItems: 'stretch',
                }}
            >
                <Box
                    sx={{
                        ...tileSx,
                        gridColumn: { xs: '1 / -1', sm: '1 / -1', md: 'auto' },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: 28, sm: 34, md: 36 },
                            fontWeight: 800,
                            color: 'text.primary',
                            lineHeight: 1.1,
                            letterSpacing: '-0.02em',
                            wordBreak: 'break-word',
                        }}
                    >
                        {formatCurrency(data.combinedTotal)}
                    </Typography>
                    <Box
                        sx={{
                            fontSize: 13,
                            color: 'text.secondary',
                            mt: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 0.5,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 600,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.25,
                                color: trendColor,
                            }}
                        >
                            {data.trend.direction === 'up'
                                ? '↑'
                                : data.trend.direction === 'down'
                                  ? '↓'
                                  : '→'}{' '}
                            {formatCurrency(data.trend.value)}
                        </Box>
                        <Box component="span">{data.trend.label}</Box>
                    </Box>
                </Box>

                <Box
                    role="button"
                    tabIndex={0}
                    onClick={goToRevenue}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            goToRevenue();
                        }
                    }}
                    sx={detailCardSx}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: 18, sm: 20, md: 22 },
                            fontWeight: 700,
                            color: 'text.primary',
                            lineHeight: 1.2,
                            letterSpacing: '-0.01em',
                            wordBreak: 'break-word',
                        }}
                    >
                        {formatCurrency(data.unpaidContractsTotal)}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: 'text.secondary',
                            mt: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            lineHeight: 1.4,
                            flexWrap: 'wrap',
                        }}
                    >
                        {data.unpaidContractsCount} unpaid contracts
                        <ArrowForward sx={{ fontSize: 12 }} />
                    </Typography>
                </Box>

                <Box
                    role="button"
                    tabIndex={0}
                    onClick={goToRevenue}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            goToRevenue();
                        }
                    }}
                    sx={detailCardSx}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: 18, sm: 20, md: 22 },
                            fontWeight: 700,
                            color: 'text.primary',
                            lineHeight: 1.2,
                            letterSpacing: '-0.01em',
                            wordBreak: 'break-word',
                        }}
                    >
                        {formatCurrency(data.unconvertedOffersTotal)}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: 'text.secondary',
                            mt: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            lineHeight: 1.4,
                            flexWrap: 'wrap',
                        }}
                    >
                        {data.unconvertedOffersCount} unconverted offers
                        <ArrowForward sx={{ fontSize: 12 }} />
                    </Typography>
                </Box>
            </Box>
        </SectionCard>
    );
};

export default RevenueAtRiskSection;
